import React from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheck, Lock, Users, Server, Database, Archive,
  Eraser, FileLock2, Megaphone, Mail, ExternalLink, FileText, Download,
} from 'lucide-react';
import { PageSEO } from '../PageSEO';
import { SiteJsonLd } from '../institutional/SiteJsonLd';
import { InstitutionalNavbar } from '../institutional/InstitutionalNavbar';
import { InstitutionalFooter } from '../institutional/InstitutionalFooter';
import { ENTITY } from '../../lib/entity';

/**
 * Phase 99A.1 — Trust Center in the public light direction.
 *
 * Layout: navbar → light hero (paper, editorial Fraunces heading, italic
 * gold accent) → factual trust sections as light panel cards → downloads
 * → institutional footer.
 *
 * Hard rules (preserved from Phase 90):
 *   - No SOC2/ISO/PCI/HIPAA/etc certification claims unless audited.
 *   - No "bank-grade" / "military-grade" / "unhackable" / "fully secure" language.
 *   - No fake uptime SLAs, no fabricated 24/7 promises.
 *   - No internal topology / vendor / stack leakage.
 *   - No customer logos, no inflated stats, no fake testimonials.
 */

const TrustCard = ({ icon: Icon, title, children, testId }) => (
  <article
    className="rounded-xl p-6 lg:p-7"
    style={{ background: 'var(--gpc-panel)', border: '1px solid var(--gpc-line)' }}
    data-testid={testId}
  >
    <div className="flex items-center gap-3 mb-3">
      <div
        className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
        style={{ background: 'rgba(140,107,46,0.10)', border: '1px solid rgba(140,107,46,0.18)' }}
      >
        <Icon className="w-4 h-4" style={{ color: 'var(--gpc-ink-soft)' }} />
      </div>
      <h2
        style={{
          fontFamily: 'Space Grotesk, system-ui, sans-serif',
          fontWeight: 600,
          fontSize: '1.05rem',
          color: 'var(--gpc-ink)',
        }}
      >
        {title}
      </h2>
    </div>
    <div className="trust-body" style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: '14px', lineHeight: 1.6, color: 'var(--gpc-ink-soft)' }}>
      {children}
    </div>
  </article>
);

const Bullets = ({ items }) => (
  <ul className="space-y-1.5 mt-1">
    {items.map((t, i) => (
      <li key={i} className="flex gap-2">
        <span aria-hidden="true" style={{ color: 'var(--gpc-ink-soft)', flexShrink: 0 }}>·</span>
        <span>{t}</span>
      </li>
    ))}
  </ul>
);

export const TrustCenter = () => (
  <div className="min-h-screen" style={{ background: 'var(--gpc-paper)' }} data-testid="trust-center-page">
    <PageSEO
      title="Trust Center — GP Capital"
      description="Institutional trust, security, hosting, retention, and incident response overview for GP Capital's wholesale capital platform."
      path="/trust"
      referrerPolicy="strict-origin"
    />
    <SiteJsonLd page="security" />
    <InstitutionalNavbar />

    <div className="pt-20">
      {/* Light hero */}
      <section style={{ background: 'var(--gpc-paper)', borderBottom: '1px solid var(--gpc-line-soft)' }} data-testid="trust-hero">
        <div className="max-w-5xl mx-auto px-6 lg:px-8" style={{ padding: 'clamp(3rem, 6vw, 5rem) clamp(1.5rem, 4vw, 4rem)' }}>
          <p className="gpc-eyebrow" style={{ marginBottom: '14px' }}>Trust Center</p>
          <h1
            data-testid="trust-center-title"
            style={{
              fontFamily: 'Fraunces, "Playfair Display", Georgia, serif',
              fontWeight: 400,
              fontSize: 'clamp(2.25rem, 4.4vw, 3.4rem)',
              lineHeight: 1.1,
              letterSpacing: '-0.015em',
              color: 'var(--gpc-ink)',
              marginBottom: '14px',
            }}
          >
            Institutional controls for{' '}
            <span style={{ color: 'var(--gpc-ink-soft)', fontStyle: 'italic' }}>private capital workflows.</span>
          </h1>
          <p style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: '1.075rem', color: 'var(--gpc-ink-soft)', maxWidth: '40rem', lineHeight: 1.6 }}>
            For procurement, compliance and IT diligence reviewers. Content here is factual, conservative, and verifiable on request — no third-party certifications are claimed that are not currently held.
          </p>
        </div>
      </section>

      {/* Truthfulness disclaimer */}
      <section style={{ background: 'var(--gpc-paper)' }}>
        <div className="max-w-5xl mx-auto px-6 lg:px-8 mt-10">
          <div
            className="rounded-xl p-5"
            style={{ background: 'var(--gpc-panel)', border: '1px solid var(--gpc-line)' }}
            data-testid="trust-center-disclaimer"
          >
            <p style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: '14px', color: 'var(--gpc-ink-soft)', lineHeight: 1.6 }}>
              GP Capital does not claim third-party certifications it does not currently hold. Where a control is in place but not yet externally audited, this page says so. Where a control is on the roadmap, it is labelled as such. We will gladly walk an institutional counterparty through specifics under NDA.
            </p>
          </div>
        </div>
      </section>

      {/* Trust grid */}
      <section className="py-12" style={{ background: 'var(--gpc-paper)' }}>
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-5">
            <TrustCard icon={ShieldCheck} title="Security overview" testId="trust-section-overview">
              <p>
                GP Capital operates a wholesale capital platform serving sophisticated counterparties (lenders, family offices, sponsors, intermediaries). Platform access is permissioned, NDA-gated, and role-based. Public-facing material on this site is intentionally limited — operational and counterparty information lives behind authenticated access only.
              </p>
            </TrustCard>

            <TrustCard icon={Lock} title="Authentication & access" testId="trust-section-auth">
              <Bullets items={[
                'Email + password authentication with bcrypt-hashed credentials (work factor ≥ 12).',
                'Two-factor authentication (TOTP) available; required for internal users and operations roles.',
                'Trusted-device sessions with explicit revocation; lockout after consecutive failed attempts.',
                'Passwordless / passkey login: roadmap item, not yet generally available.',
                'Role-based access control with strict internal / external boundaries; lender-to-lender data isolation enforced at every endpoint.',
                'Audit logging for authentication, permission changes, and administrative actions.',
              ]} />
            </TrustCard>

            <TrustCard icon={Users} title="Role-based access" testId="trust-section-rbac">
              <p>
                Every counterparty user has exactly one role at a time (borrower, lender, family office, broker, internal team, admin, super-admin). Each deal-room access grant is a separate record with its own NDA / agreement status, visibility scope, and revocation timestamp. External users only ever see their own participation — never another lender&apos;s identity, response, mandate detail, or matching score.
              </p>
            </TrustCard>

            <TrustCard icon={Server} title="Hosting & infrastructure" testId="trust-section-hosting">
              <Bullets items={[
                'Application is hosted on managed cloud infrastructure with hardened images and least-privilege service identities.',
                'All public traffic is served over TLS 1.2+ with HSTS.',
                'Internal traffic between application and database tiers is private and authenticated.',
                'We do not disclose specific vendor names, regions, or topology publicly. Diligence reviewers can receive this under NDA.',
                'Production secrets are managed outside of source control and rotated on personnel changes.',
              ]} />
            </TrustCard>

            <TrustCard icon={Database} title="Data storage & encryption" testId="trust-section-data-storage">
              <Bullets items={[
                'Counterparty and deal data are stored in a managed document database with access controlled per service identity.',
                'Documents and uploads are stored in a managed object store with server-side encryption at rest.',
                'In transit, data is protected by TLS 1.2+ on every public hop and by authenticated channels internally.',
                'Customer-managed encryption keys (CMEK / BYOK) are not currently offered. This is on the roadmap for enterprise tier.',
              ]} />
            </TrustCard>

            <TrustCard icon={Archive} title="Backup & disaster recovery" testId="trust-section-bcdr">
              <Bullets items={[
                'Production databases are backed up daily with point-in-time recovery enabled.',
                'Backups are encrypted at rest and retained for a rolling window.',
                'Recovery is exercised periodically as part of internal operations review.',
                'We do not publish a contractual RTO / RPO publicly. Specific recovery objectives can be agreed bilaterally with institutional counterparties.',
              ]} />
            </TrustCard>

            <TrustCard icon={Eraser} title="Data retention & deletion" testId="trust-section-retention">
              <Bullets items={[
                'Counterparty and deal data are retained while the relationship is active and for a defined period thereafter, consistent with our legal and regulatory obligations.',
                'Authenticated users can request deletion of personal data through the portal. Some records (audit logs, regulatory filings) are retained for the statutory minimum period.',
                'Document deletion respects participant agreement and audit-trail requirements — deleted material is removed from active surfaces but a tamper-evident audit reference is retained.',
                'Detailed retention schedules are available to counterparties under NDA on request.',
              ]} />
            </TrustCard>

            <TrustCard icon={FileLock2} title="Privacy, confidentiality & DPA" testId="trust-section-privacy">
              <p>
                Deal content, counterparty identities, and participant responses are confidential by default. Platform access is gated by an NDA / platform agreement. We process personal data in line with the{' '}
                <Link to="/privacy" className="trust-inline-link">Privacy Policy</Link>{' '}
                and operate under the{' '}
                <Link to="/terms" className="trust-inline-link">Terms of Service</Link>,{' '}
                <Link to="/platform-terms" className="trust-inline-link">Platform Terms</Link>, and{' '}
                <Link to="/disclosures" className="trust-inline-link">Disclosures</Link>.
              </p>
              <p className="mt-3">
                A Data Processing Addendum (DPA) is available on request for institutional counterparties whose internal policy requires one.
              </p>
            </TrustCard>

            <TrustCard icon={Megaphone} title="Incident response" testId="trust-section-incident">
              <Bullets items={[
                'Operational incidents are tracked in an internal incident register with severity, owner, and timeline.',
                'Material incidents that affect a counterparty are communicated directly to the affected party as soon as facts are established — not via public channels.',
                'Lessons-learned reviews follow material incidents and feed roadmap-level remediation.',
                'We do not currently maintain a public status page. This is on the roadmap.',
              ]} />
            </TrustCard>

            <TrustCard icon={Mail} title="Responsible disclosure & contact" testId="trust-section-disclosure">
              <p>
                Security researchers and counterparties who identify a potential vulnerability are encouraged to report it via the{' '}
                <Link to="/security" className="trust-inline-link" data-testid="trust-link-security">Security &amp; Responsible Disclosure page</Link>{' '}
                or directly to the contact listed in our{' '}
                <a
                  href="/.well-known/security.txt"
                  className="trust-inline-link inline-flex items-center gap-1"
                  data-testid="trust-link-security-txt"
                >
                  security.txt <ExternalLink className="w-3 h-3" />
                </a>{' '}file. We aim to acknowledge reports within two business days.
              </p>
              <p className="mt-3 text-xs">
                For all other procurement / compliance enquiries, please contact{' '}
                <a
                  href={`mailto:${ENTITY.contact.general}?subject=${encodeURIComponent(ENTITY.mailSubjects.compliance)}`}
                  className="trust-inline-link"
                  data-testid="trust-link-procurement-email"
                >
                  {ENTITY.contact.general}
                </a>{' '}with your reviewer details and we will route to the right team.
              </p>
            </TrustCard>
          </div>

          {/* Downloads */}
          <div
            className="mt-10 rounded-xl p-6 lg:p-7"
            style={{ background: 'var(--gpc-panel)', border: '1px solid var(--gpc-line)' }}
            data-testid="trust-section-downloads"
          >
            <div className="flex items-center gap-3 mb-3">
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                style={{ background: 'rgba(140,107,46,0.10)', border: '1px solid rgba(140,107,46,0.18)' }}
              >
                <Download className="w-4 h-4" style={{ color: 'var(--gpc-ink-soft)' }} />
              </div>
              <h2 style={{ fontFamily: 'Space Grotesk, system-ui, sans-serif', fontWeight: 600, fontSize: '1.05rem', color: 'var(--gpc-ink)' }}>
                Downloadable materials
              </h2>
            </div>
            <p style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: '14px', color: 'var(--gpc-ink-soft)', lineHeight: 1.6 }}>
              The following PDFs are generated from the same factual content as this page and are safe to share with procurement / compliance reviewers.
            </p>
            <ul className="grid sm:grid-cols-2 gap-3 mt-4" data-testid="trust-downloads-list">
              <li>
                <a
                  href="/api/public/trust/overview.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="trust-download"
                  data-testid="trust-download-overview-pdf"
                >
                  <FileText className="w-4 h-4 shrink-0" style={{ color: 'var(--gpc-ink-soft)' }} />
                  <span className="flex-1 min-w-0">
                    <span className="block font-medium text-sm" style={{ color: 'var(--gpc-ink)' }}>Trust Center overview (PDF)</span>
                    <span className="block text-xs" style={{ color: 'var(--gpc-ink-soft)' }}>One-page summary of this page</span>
                  </span>
                  <Download className="w-3.5 h-3.5" style={{ color: 'var(--gpc-ink-soft)' }} />
                </a>
              </li>
              <li>
                <a
                  href="/api/public/trust/dpa-request.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="trust-download"
                  data-testid="trust-download-dpa-request-pdf"
                >
                  <FileLock2 className="w-4 h-4 shrink-0" style={{ color: 'var(--gpc-ink-soft)' }} />
                  <span className="flex-1 min-w-0">
                    <span className="block font-medium text-sm" style={{ color: 'var(--gpc-ink)' }}>DPA Request Form (PDF)</span>
                    <span className="block text-xs" style={{ color: 'var(--gpc-ink-soft)' }}>
                      Form to request the executed DPA template — this is not the DPA itself
                    </span>
                  </span>
                  <Download className="w-3.5 h-3.5" style={{ color: 'var(--gpc-ink-soft)' }} />
                </a>
              </li>
            </ul>
            <p
              className="text-xs mt-3"
              style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--gpc-ink-soft)' }}
              data-testid="trust-downloads-disclaimer"
            >
              <strong style={{ color: 'var(--gpc-ink-soft)' }}>Plainly:</strong> the DPA Request Form is a form, not an executed DPA. The executed institutional DPA is reviewed and signed on a per-counterparty basis under NDA. Request via the form or by emailing the contact above.
            </p>
          </div>

          {/* Related policies */}
          <div
            className="mt-10 p-6 rounded-xl"
            style={{ background: 'var(--gpc-panel)', border: '1px solid var(--gpc-line)' }}
            data-testid="trust-section-related"
          >
            <p className="gpc-eyebrow" style={{ marginBottom: '10px' }}>Related policies &amp; documents</p>
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-2.5" style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: '14px' }}>
              <li><Link to="/privacy" className="trust-inline-link" data-testid="trust-link-privacy">Privacy Policy →</Link></li>
              <li><Link to="/terms" className="trust-inline-link" data-testid="trust-link-terms">Terms of Service →</Link></li>
              <li><Link to="/platform-terms" className="trust-inline-link" data-testid="trust-link-platform-terms">Platform Terms →</Link></li>
              <li><Link to="/disclosures" className="trust-inline-link" data-testid="trust-link-disclosures">Disclosures →</Link></li>
              <li><Link to="/security" className="trust-inline-link" data-testid="trust-link-security-page">Security &amp; Responsible Disclosure →</Link></li>
              <li><a href="/.well-known/security.txt" className="trust-inline-link inline-flex items-center gap-1" data-testid="trust-link-security-txt-2">security.txt → <ExternalLink className="w-3 h-3" /></a></li>
            </ul>
          </div>

          <p
            className="mt-10"
            style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: '12px', color: 'var(--gpc-ink-soft)', maxWidth: '60rem', borderTop: '1px solid var(--gpc-line-soft)', paddingTop: '20px' }}
            data-testid="trust-center-footnote"
          >
            This page is a summary, not a substitute for the underlying agreements. The Platform Terms, Privacy Policy, Disclosures and any deal-room NDA govern the relationship between {ENTITY.brand} and its counterparties.
          </p>
        </div>
      </section>
    </div>

    <InstitutionalFooter />

    <style>{`
      .trust-inline-link {
        color: var(--gpc-ink);
        text-decoration: underline;
        text-decoration-color: rgba(140,107,46,0.45);
        text-underline-offset: 3px;
        transition: color 0.2s ease, text-decoration-color 0.2s ease;
      }
      .trust-inline-link:hover {
        color: var(--gpc-gold);
        text-decoration-color: var(--gpc-gold);
      }
      .trust-download {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 12px;
        border-radius: 10px;
        border: 1px solid var(--gpc-line);
        background: var(--gpc-paper);
        transition: border-color 0.2s ease, background-color 0.2s ease;
      }
      .trust-download:hover {
        border-color: var(--gpc-line-strong);
        background: var(--gpc-paper-2);
      }
    `}</style>
  </div>
);

export default TrustCenter;
